import { useMemo, useState } from "react";
import { FileText } from "lucide-react";
import { cleaningCatalogConfig, getSiteContent } from "@/data/siteContent";
import { useLanguage } from "@/context/LanguageContext";

export default function CleaningCatalogPage() {
  const { language } = useLanguage();
  const content = getSiteContent(language);
  const [activeSectionKey, setActiveSectionKey] = useState("");

  const sectionGroupsWithImages = useMemo(
    () =>
      content.cleaningCatalog.sectionGroups.map((group) => {
        const images = Array.from({ length: group.endPage - group.startPage + 1 }, (_, idx) => {
          const pageNumber = String(group.startPage + idx).padStart(3, "0");
          return `${cleaningCatalogConfig.pagesFolder}/page-${pageNumber}.jpg`;
        });

        return { ...group, images };
      }),
    [content.cleaningCatalog.sectionGroups],
  );

  return (
    <section className="bg-white py-16 md:py-24" data-testid="cleaning-catalog-page">
      <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center" data-testid="cleaning-catalog-header">
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl" data-testid="cleaning-catalog-title">
            {content.cleaningCatalog.title}
          </h1>
          <p className="mt-4 text-base text-slate-600 md:text-lg" data-testid="cleaning-catalog-subtitle">
            {content.cleaningCatalog.subtitle}
          </p>
        </div>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="cleaning-catalog-sections-block">
          <div className="mb-3 flex items-center gap-2">
            <FileText className="h-5 w-5 text-secondary" />
            <h2 className="text-lg font-bold text-slate-900" data-testid="cleaning-catalog-sections-title">
              {content.cleaningCatalog.sectionsTitle}
            </h2>
          </div>
          <div className="space-y-3" data-testid="cleaning-catalog-sections-list">
            {sectionGroupsWithImages.map((section, index) => (
              <div
                key={`cleaning-section-${section.key}`}
                className="rounded-xl border border-slate-200 bg-white p-3"
                data-testid={`cleaning-catalog-section-wrapper-${index}`}
              >
                <button
                  type="button"
                  onClick={() => setActiveSectionKey((prev) => (prev === section.key ? "" : section.key))}
                  className={`w-full rounded-lg border px-3 py-2 text-sm font-semibold transition-colors duration-300 ${
                    activeSectionKey === section.key
                      ? "border-secondary bg-secondary/10 text-secondary"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid={`cleaning-catalog-section-item-${index}`}
                >
                  {section.title}
                </button>

                {activeSectionKey === section.key ? (
                  <div className="mt-3" data-testid={`cleaning-catalog-section-expanded-${index}`}>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-xs text-slate-500" data-testid={`cleaning-catalog-group-pages-${index}`}>
                        {language === "ar"
                          ? `الصفحات: ${section.startPage} - ${section.endPage}`
                          : `Pages: ${section.startPage} - ${section.endPage}`}
                      </p>
                      <button
                        type="button"
                        onClick={() => setActiveSectionKey("")}
                        className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                        data-testid={`cleaning-catalog-close-section-button-${index}`}
                      >
                        {language === "ar" ? "إغلاق القسم" : "Close Section"}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-3" data-testid={`cleaning-catalog-section-images-grid-${index}`}>
                      {section.images.map((imgUrl, imgIndex) => {
                        const pageNum = section.startPage + imgIndex;
                        return (
                          <div
                            key={`catalog-preview-${section.key}-${imgIndex}`}
                            className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
                          >
                            <img
                              src={imgUrl}
                              alt={`catalog page ${pageNum}`}
                              className="w-full object-contain"
                              loading="lazy"
                              data-testid={`cleaning-catalog-preview-image-${pageNum}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
